import {
  ApiError,
  formatMoney,
  formatUsPhone,
  type InventoryCost,
  type Order,
  type OrderStatus,
  queryKeys,
  type ShipmentView,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  humanizeStatus,
  ORDER_STATUS_TONE,
  Skeleton,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import {
  ArrowLeft,
  CalendarClock,
  Gift,
  Package,
  Paperclip,
  Printer,
  Receipt,
  Truck,
} from 'lucide-react'
import { useState } from 'react'
import { Page, PageHeader } from '../components/app-shell.js'
import { MarkImportantButton } from '../components/mark-important.js'
import { canFlagLine, OrderIdentityFlagDialog } from '../components/order-identity-flag-dialog.js'
import { canReturnItems, OrderReturnDialog } from '../components/order-return-dialog.js'
import { actionLabel, OrderTransitionDialog } from '../components/order-transition-dialog.js'
import { AddSaleCostDialog, type SaleCostLine } from '../components/sale-cost-dialog.js'
import { ShipmentPurchaseDialog } from '../components/shipment-purchase-dialog.js'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { printOrderReceipt } from '../lib/receipt-print.js'

interface OrderEvent {
  id: string
  fromStatus: OrderStatus | null
  toStatus: OrderStatus
  actorType: string
  actorLabel: string | null
  note: string | null
  /** Set when a linked customer's saved card paid (t1117). */
  payment: { cardOwnerName: string; cardLast4: string | null } | null
  createdAt: string
}

interface OrderRentalRow {
  id: string
  rentalNumber: string
  status: string
  feeCents: number
  depositCents: number
}

const CHANNEL_LABEL: Record<string, string> = {
  web: 'Website',
  pos: 'In store',
  kiosk: 'Kiosk',
  phone: 'Phone',
}

/**
 * One order, and everything that can be done to it.
 *
 * The available moves come from the API rather than being listed here: the
 * order state machine lives in one place, and a button offering a transition
 * the server will refuse is worse than no button.
 */
export function OrderDetailPage() {
  const { orderId } = useParams({ from: '/authed/orders/$orderId' })
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const [moving, setMoving] = useState<OrderStatus | null>(null)
  const [buyingLabel, setBuyingLabel] = useState(false)
  const [returning, setReturning] = useState(false)
  const [costLine, setCostLine] = useState<SaleCostLine | null>(null)
  const [flagging, setFlagging] = useState<{ itemId: string; mode: 'flag' | 'confirm' } | null>(
    null,
  )

  const { data: order, isLoading } = useQuery({
    queryKey: queryKeys.orders.detail(orderId),
    queryFn: () => api.get<Order>(`/orders/${orderId}`),
  })

  const { data: transitions } = useQuery({
    queryKey: queryKeys.orders.transitions(orderId),
    queryFn: () =>
      api.get<{ from: OrderStatus; to: OrderStatus[] }>(`/orders/${orderId}/transitions`),
    enabled: Boolean(order),
  })

  const { data: events } = useQuery({
    queryKey: queryKeys.orders.events(orderId),
    queryFn: () => api.get<OrderEvent[]>(`/orders/${orderId}/events`),
    enabled: Boolean(order),
  })

  const { data: shipments } = useQuery({
    queryKey: queryKeys.orders.shipments(orderId),
    queryFn: () => api.get<ShipmentView[]>(`/orders/${orderId}/shipments`),
    enabled: Boolean(order) && order?.fulfillmentMethod === 'shipping',
  })

  // A rental opened alongside this sale (t0943) — a purchase-only order
  // simply comes back empty.
  const { data: rentals } = useQuery({
    queryKey: queryKeys.rentals.list({ orderId }),
    queryFn: () =>
      api.get<{ items: OrderRentalRow[] }>('/rentals', { query: { orderId, pageSize: 20 } }),
    enabled: Boolean(order),
  })

  // t1317: costs added to this order's lines after the sale.
  const { data: saleCosts } = useQuery({
    queryKey: queryKeys.purchasing.costs({ orderId }),
    queryFn: () =>
      api.get<{ items: InventoryCost[] }>('/costs', { query: { orderId, pageSize: 200 } }),
    enabled: Boolean(order) && can('inventory:read'),
  })

  const refundShipment = useMutation({
    mutationFn: (shipmentId: string) =>
      api.post(`/orders/${orderId}/shipments/${shipmentId}/refund`, {}),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.shipments(orderId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(orderId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.orders.events(orderId) }),
      ])
      toast.success('Label refunded')
    },
    onError: (error: Error) =>
      toast.error('Could not refund', error instanceof ApiError ? error.message : undefined),
  })

  if (isLoading || !order) {
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Page>
    )
  }

  const moves = transitions?.to ?? []

  return (
    <Page>
      <div className="pb-3">
        <Link
          to="/orders"
          className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" /> All orders
        </Link>
      </div>

      <PageHeader
        title={order.orderNumber}
        description={`${CHANNEL_LABEL[order.channel] ?? order.channel} · ${
          order.fulfillmentMethod === 'shipping'
            ? 'Shipping'
            : order.fulfillmentMethod === 'none'
              ? 'Plan only'
              : 'Pickup'
        } · placed ${new Date(order.createdAt).toLocaleString('en-US', {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MarkImportantButton itemType="order" itemId={orderId} />
            <Badge tone={ORDER_STATUS_TONE[order.status] ?? 'neutral'}>
              {humanizeStatus(order.status)}
            </Badge>
            <Button size="sm" variant="secondary" onClick={() => printOrderReceipt(order)}>
              <Printer className="size-3.5" aria-hidden="true" /> Print receipt
            </Button>
            {can('orders:write') &&
              moves.map((next) => (
                <Button
                  key={next}
                  size="sm"
                  variant={next === 'cancelled' || next === 'refunded' ? 'ghost' : 'primary'}
                  onClick={() => setMoving(next)}
                >
                  {actionLabel(next)}
                </Button>
              ))}
            {can('orders:refund') && canReturnItems(order) ? (
              <Button size="sm" variant="ghost" onClick={() => setReturning(true)}>
                Return items
              </Button>
            ) : null}
          </div>
        }
      />

      {moves.length === 0 ? (
        <Alert tone="info" className="mb-4">
          This order is closed. Nothing further can be done to it.
        </Alert>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="size-4" aria-hidden="true" /> Items
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-2xs uppercase tracking-wide text-foreground-subtle">
                  <tr>
                    <th className="px-4 py-2 text-start font-medium">Product</th>
                    <th className="px-4 py-2 text-end font-medium">Qty</th>
                    <th className="px-4 py-2 text-end font-medium">Unit</th>
                    <th className="px-4 py-2 text-end font-medium">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item) => (
                    <tr key={item.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-2.5">
                        <p className="font-medium">{item.productName}</p>
                        <p className="font-identifier text-2xs text-foreground-muted">
                          {item.sku}
                          {/* The IMEI is what ties this line to a physical
                              handset — the one fact staff are asked for most. */}
                          {item.imei ? ` · IMEI ${item.imei}` : ''}
                        </p>
                        {item.sourceLocationName ? (
                          <Badge tone="info" className="mt-1">
                            from {item.sourceLocationName}
                          </Badge>
                        ) : null}
                        {item.returnedQty > 0 ? (
                          <Badge tone="warning" className="mt-1">
                            Returned ×{item.returnedQty}
                          </Badge>
                        ) : null}
                        {item.identityFlag && !item.identityFlag.resolvedAt ? (
                          <div className="mt-1 space-y-0.5 text-2xs">
                            <Badge tone="warning">Unverified unit</Badge>
                            {item.identityFlag.candidates.length > 0 ? (
                              <p className="font-identifier text-foreground-muted">
                                Could be: {item.identityFlag.candidates.join(', ')}
                              </p>
                            ) : null}
                            {item.identityFlag.pool ? (
                              <p className="text-foreground-muted">
                                From: {item.identityFlag.pool}
                              </p>
                            ) : null}
                            {item.identityFlag.note ? (
                              <p className="text-foreground-muted">{item.identityFlag.note}</p>
                            ) : null}
                          </div>
                        ) : item.identityFlag ? (
                          <p className="mt-1 text-2xs text-foreground-muted">
                            Unit confirmed by {item.identityFlag.resolvedByLabel ?? 'staff'}
                            {item.identityFlag.resolutionNote
                              ? ` — ${item.identityFlag.resolutionNote}`
                              : ''}
                          </p>
                        ) : null}
                        {saleCosts?.items
                          .filter((cost) => cost.orderItemId === item.id)
                          .map((cost) => (
                            <p key={cost.id} className="mt-1 text-2xs text-foreground-muted">
                              Cost: {formatMoney(cost.amountCents)} · {cost.label}
                              {cost.vendor ? ` · ${cost.vendor}` : ''}
                              {cost.invoiceRef ? ` · #${cost.invoiceRef}` : ''}
                              {cost.document ? (
                                <a
                                  href={`${apiBaseUrl}/costs/${cost.id}/document`}
                                  target="_blank"
                                  rel="noreferrer"
                                  aria-label={`View the attached ${cost.document.filename}`}
                                  className="ms-1 inline-block align-middle hover:text-foreground"
                                >
                                  <Paperclip className="size-3" />
                                </a>
                              ) : null}
                            </p>
                          ))}
                        {can('inventory:write') ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="mt-1"
                            onClick={() =>
                              setCostLine({
                                id: item.id,
                                productName: item.productName,
                                qty: item.qty,
                              })
                            }
                          >
                            Add cost
                          </Button>
                        ) : null}
                        {can('orders:write') && canFlagLine(order, item) ? (
                          <div className="mt-1 flex gap-1">
                            {item.identityFlag && !item.identityFlag.resolvedAt ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => setFlagging({ itemId: item.id, mode: 'confirm' })}
                                >
                                  Confirm item
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setFlagging({ itemId: item.id, mode: 'flag' })}
                                >
                                  Edit
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setFlagging({ itemId: item.id, mode: 'flag' })}
                              >
                                Mark as possibly wrong
                              </Button>
                            )}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5 text-end tabular">{item.qty}</td>
                      <td className="px-4 py-2.5 text-end tabular text-foreground-muted">
                        {formatMoney(item.unitPriceCents)}
                      </td>
                      <td className="px-4 py-2.5 text-end tabular font-medium">
                        {formatMoney(item.lineTotalCents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>History</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {(events ?? []).map((event) => (
                  <li key={event.id} className="flex gap-3 text-sm">
                    <span
                      className="mt-1.5 size-1.5 shrink-0 rounded-full bg-border-strong"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p>
                        {event.fromStatus ? (
                          <>
                            <span className="text-foreground-muted">
                              {humanizeStatus(event.fromStatus)}
                            </span>{' '}
                            →{' '}
                          </>
                        ) : null}
                        <span className="font-medium">{humanizeStatus(event.toStatus)}</span>
                      </p>
                      <p className="text-2xs text-foreground-muted">
                        {event.actorLabel ?? event.actorType} ·{' '}
                        {new Date(event.createdAt).toLocaleString('en-US', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </p>
                      {event.payment ? (
                        <p className="mt-0.5 text-xs text-foreground-muted">
                          Paid with {event.payment.cardOwnerName}'s card
                          {event.payment.cardLast4 ? ` •••• ${event.payment.cardLast4}` : ''}
                        </p>
                      ) : null}
                      {event.note ? (
                        <p className="mt-0.5 text-xs text-foreground-muted">{event.note}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="size-4" aria-hidden="true" /> Money
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <Row label="Subtotal" value={formatMoney(order.subtotalCents)} />
              {order.discountCents > 0 ? (
                <Row
                  label={order.couponCode ? `Discount (${order.couponCode})` : 'Discount'}
                  value={`−${formatMoney(order.discountCents)}`}
                />
              ) : null}
              {order.shippingCents > 0 ? (
                <Row label="Shipping" value={formatMoney(order.shippingCents)} />
              ) : null}
              {order.taxCents > 0 || order.taxExemptApplied ? (
                <Row
                  label="Tax"
                  value={order.taxExemptApplied ? 'Exempt' : formatMoney(order.taxCents)}
                />
              ) : null}
              <div className="border-t border-border pt-2">
                <Row label="Total" value={formatMoney(order.totalCents)} strong />
              </div>
              {(order.credits ?? []).map((credit) => (
                <Row
                  key={`${credit.provider}-${credit.label}-${credit.amountCents}-${credit.status}`}
                  label={credit.status === 'pending' ? `${credit.label} (held)` : credit.label}
                  value={`−${formatMoney(credit.amountCents)}`}
                />
              ))}
              <Row label="Paid" value={formatMoney(order.paidCents)} />
              {order.refundedCents > 0 ? (
                <Row label="Refunded" value={formatMoney(order.refundedCents)} tone="danger" />
              ) : null}
              {order.returns.length > 0 ? (
                <Row
                  label="Restocking fees kept"
                  value={formatMoney(order.returns.reduce((sum, r) => sum + r.feeCents, 0))}
                />
              ) : null}
              {order.balanceDueCents > 0 ? (
                <Row
                  label="Balance due"
                  value={formatMoney(order.balanceDueCents)}
                  tone="warning"
                />
              ) : null}
            </CardContent>
          </Card>

          {(order.giftCards ?? []).length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gift className="size-4" aria-hidden="true" /> Gift cards on this order
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {(order.giftCards ?? []).map((card) => (
                  <div key={card.id} className="flex items-center justify-between gap-3">
                    <span>
                      <span className="font-mono">…{card.codeLast4}</span>{' '}
                      <span className="text-foreground-muted">
                        · {formatMoney(card.initialCents)} · {card.kind}
                      </span>
                    </span>
                    <Badge tone={card.status === 'active' ? 'success' : 'neutral'}>
                      {card.status === 'pending' ? 'Awaiting payment' : card.status}
                    </Badge>
                  </div>
                ))}
                <p className="text-xs text-foreground-muted">
                  Open Gift cards to show a card's code, or to mark a physical card as ready once
                  its code is on it.
                </p>
                <Link to="/gift-cards" className="text-primary hover:underline">
                  Open Gift cards
                </Link>
              </CardContent>
            </Card>
          ) : null}

          {order.returns.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Returns</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {order.returns.map((ret) => (
                  <div
                    key={ret.id}
                    className="space-y-1 border-b border-border pb-3 last:border-0 last:pb-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">{formatMoney(ret.refundCents)} refunded</span>
                      <span className="text-2xs text-foreground-muted">
                        {new Date(ret.createdAt).toLocaleDateString('en-US', {
                          dateStyle: 'medium',
                        })}
                      </span>
                    </div>
                    {ret.feeCents > 0 ? (
                      <p className="text-2xs text-foreground-muted">
                        {formatMoney(ret.feeCents)} restocking fee kept
                      </p>
                    ) : null}
                    <p className="text-2xs text-foreground-muted">{ret.actorLabel ?? 'Staff'}</p>
                    {ret.note ? <p className="text-xs text-foreground-muted">{ret.note}</p> : null}
                  </div>
                ))}
              </CardContent>
            </Card>
          ) : null}

          {rentals && rentals.items.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarClock className="size-4" aria-hidden="true" /> Rentals on this sale
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {rentals.items.map((rental) => (
                  <Link
                    key={rental.id}
                    to="/rentals/$rentalId"
                    params={{ rentalId: rental.id }}
                    className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-surface-sunken"
                  >
                    <span className="font-medium">{rental.rentalNumber}</span>
                    <span className="tabular text-foreground-muted">
                      {rental.feeCents > 0 ? `Fee ${formatMoney(rental.feeCents)}` : null}
                      {rental.feeCents > 0 && rental.depositCents > 0 ? ' · ' : ''}
                      {rental.depositCents > 0
                        ? `Deposit ${formatMoney(rental.depositCents)}`
                        : null}
                    </span>
                  </Link>
                ))}
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Customer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 text-sm">
              {order.customerId ? (
                <>
                  <Link
                    to="/customers/$customerId"
                    params={{ customerId: order.customerId }}
                    className="font-medium hover:underline"
                  >
                    {order.customerName}
                  </Link>
                  {order.customerPhone ? (
                    <p className="font-identifier text-xs text-foreground-muted">
                      {formatUsPhone(order.customerPhone)}
                    </p>
                  ) : null}
                </>
              ) : order.buyerName ? (
                <>
                  <p className="font-medium">{order.buyerName}</p>
                  {order.buyerPhone ? (
                    <p className="font-identifier text-xs text-foreground-muted">
                      {formatUsPhone(order.buyerPhone)}
                    </p>
                  ) : null}
                  <Badge tone="neutral" className="mt-1">
                    No account
                  </Badge>
                </>
              ) : (
                <p className="text-foreground-muted">Walk-in — no account</p>
              )}

              <p className="pt-2 text-2xs uppercase tracking-wide text-foreground-subtle">
                {order.fulfillmentMethod === 'shipping'
                  ? 'Deliver to'
                  : order.fulfillmentMethod === 'none'
                    ? 'Fulfilment'
                    : 'Collection'}
              </p>
              {order.fulfillmentMethod === 'shipping' ? (
                order.shippingAddress ? (
                  <address className="not-italic text-foreground-muted">
                    {order.shippingAddress.line1}
                    <br />
                    {order.shippingAddress.line2 ? (
                      <>
                        {order.shippingAddress.line2}
                        <br />
                      </>
                    ) : null}
                    {order.shippingAddress.city}, {order.shippingAddress.state}{' '}
                    {order.shippingAddress.postalCode}
                  </address>
                ) : (
                  <p className="text-warning">No delivery address on file</p>
                )
              ) : order.fulfillmentMethod === 'none' ? (
                <p className="text-foreground-muted">Nothing to hand over — plan only</p>
              ) : (
                <p className="text-foreground-muted">Collecting at the counter</p>
              )}
              {order.trackingNumber ? (
                <p className="font-identifier text-xs text-foreground-muted">
                  Tracking {order.trackingNumber}
                </p>
              ) : null}
            </CardContent>
          </Card>

          {order.fulfillmentMethod === 'shipping' ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="size-4" aria-hidden="true" /> Shipping label
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {(shipments ?? []).map((shipment) => (
                  <div
                    key={shipment.id}
                    className="space-y-1 border-b border-border pb-3 last:border-0 last:pb-0"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium">
                        {shipment.carrier} {shipment.serviceName}
                      </p>
                      <span className="tabular text-foreground-muted">
                        {formatMoney(shipment.rateAmountCents)}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Badge
                        tone={
                          shipment.status === 'purchased'
                            ? 'success'
                            : shipment.status === 'refunded'
                              ? 'neutral'
                              : 'warning'
                        }
                      >
                        {humanizeStatus(shipment.status)}
                      </Badge>
                      {shipment.trackingStatus ? (
                        <Badge tone="info">{humanizeStatus(shipment.trackingStatus)}</Badge>
                      ) : null}
                    </div>
                    {shipment.trackingNumber ? (
                      <p className="font-identifier text-xs text-foreground-muted">
                        {shipment.trackingNumber}
                      </p>
                    ) : null}
                    <div className="flex gap-2 pt-1">
                      {shipment.labelUrl ? (
                        <a
                          href={shipment.labelUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs text-primary hover:underline"
                        >
                          Open label
                        </a>
                      ) : null}
                      {shipment.status === 'purchased' && can('orders:write') ? (
                        <button
                          type="button"
                          className="text-xs text-danger hover:underline"
                          onClick={() => {
                            if (confirm('Refund this shipping label?')) {
                              refundShipment.mutate(shipment.id)
                            }
                          }}
                        >
                          Refund
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}

                {can('orders:write') ? (
                  <Button size="sm" variant="secondary" onClick={() => setBuyingLabel(true)}>
                    Buy shipping label
                  </Button>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          {order.note ? (
            <Card>
              <CardHeader>
                <CardTitle>Note from the customer</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-foreground-muted">{order.note}</CardContent>
            </Card>
          ) : null}
        </div>
      </div>

      <OrderTransitionDialog order={order} to={moving} onClose={() => setMoving(null)} />
      <ShipmentPurchaseDialog
        order={order}
        open={buyingLabel}
        onClose={() => setBuyingLabel(false)}
      />
      <OrderReturnDialog order={order} open={returning} onClose={() => setReturning(false)} />
      <OrderIdentityFlagDialog
        order={order}
        item={order.items.find((i) => i.id === flagging?.itemId) ?? null}
        mode={flagging?.mode ?? 'flag'}
        onClose={() => setFlagging(null)}
      />
      <AddSaleCostDialog line={costLine} orderId={orderId} onClose={() => setCostLine(null)} />
    </Page>
  )
}

function Row({
  label,
  value,
  strong,
  tone,
}: {
  label: string
  value: string
  strong?: boolean
  tone?: 'warning' | 'danger'
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-foreground-muted">{label}</span>
      <span
        className={`tabular ${strong ? 'font-semibold' : ''} ${
          tone === 'warning' ? 'text-warning' : tone === 'danger' ? 'text-danger' : ''
        }`}
      >
        {value}
      </span>
    </div>
  )
}
